import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Play,
  Pause,
  Square,
  Settings,
  Plus,
  Save
} from 'lucide-react';

interface SessionControlsProps {
  sessionStatus: 'active' | 'paused' | 'ended';
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
  onDrillChange: (drill: string) => void;
  onAddPlayer?: () => void;
  onSaveSession?: () => void;
}

const DRILL_OPTIONS = [
  { value: 'drive_practice', label: 'Drive Practice' },
  { value: 'cut_shots', label: 'Cut Shots' },
  { value: 'pull_hooks', label: 'Pull & Hook' },
  { value: 'sweep_shots', label: 'Sweep Shots' },
  { value: 'defensive_play', label: 'Defensive Play' },
  { value: 'power_hitting', label: 'Power Hitting' },
  { value: 'placement_practice', label: 'Placement Practice' },
  { value: 'running_between_wickets', label: 'Running Between Wickets' },
  { value: 'match_simulation', label: 'Match Simulation' },
  { value: 'custom_drill', label: 'Custom Drill' },
];

export function SessionControls({
  sessionStatus,
  onPause,
  onResume,
  onEnd,
  onDrillChange,
  onAddPlayer,
  onSaveSession
}: SessionControlsProps) {
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState('drive_practice');

  const handleDrillChange = (value: string) => {
    setSelectedDrill(value);
    onDrillChange(value);
  };

  const handleEndSession = () => {
    onEnd();
    setShowEndDialog(false);
  };

  return (
    <div className="flex items-center space-x-3">
      {/* Session Status Indicator */}
      <Badge 
        variant={sessionStatus === 'active' ? 'default' : 'secondary'}
        className="flex items-center space-x-1"
      >
        {sessionStatus === 'active' && (
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        )}
        <span>{sessionStatus.toUpperCase()}</span>
      </Badge>

      {/* Drill Selection */}
      <div className="flex items-center space-x-2">
        <span className="text-sm text-muted-foreground">Drill:</span>
        <Select value={selectedDrill} onValueChange={handleDrillChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select drill" />
          </SelectTrigger>
          <SelectContent>
            {DRILL_OPTIONS.map((drill) => (
              <SelectItem key={drill.value} value={drill.value}>
                {drill.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Session Control Buttons */}
      <div className="flex items-center space-x-2">
        {sessionStatus === 'active' && (
          <Button variant="outline" size="sm" onClick={onPause}>
            <Pause className="w-4 h-4 mr-2" />
            Pause
          </Button>
        )}

        {sessionStatus === 'paused' && (
          <Button variant="default" size="sm" onClick={onResume}>
            <Play className="w-4 h-4 mr-2" />
            Resume
          </Button>
        )}

        {onAddPlayer && sessionStatus !== 'ended' && (
          <Button variant="outline" size="sm" onClick={onAddPlayer}>
            <Plus className="w-4 h-4 mr-2" />
            Add Player
          </Button>
        )}

        {onSaveSession && (
          <Button variant="outline" size="sm" onClick={onSaveSession}>
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        )}

        {/* End Session Dialog */}
        <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
          <DialogTrigger asChild>
            <Button 
              variant="destructive" 
              size="sm" 
              disabled={sessionStatus === 'ended'}
            >
              <Square className="w-4 h-4 mr-2" />
              End Session
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>End Training Session</DialogTitle>
              <DialogDescription>
                Are you sure you want to end this training session? This action cannot be undone.
                All session data will be saved automatically.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Session will be saved</span>
                  <Badge variant="outline">Auto-save enabled</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Reports will be generated</span>
                  <Badge variant="outline">Auto-generate</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Players will be notified</span>
                  <Badge variant="outline">Notifications sent</Badge>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEndDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleEndSession}>
                <Square className="w-4 h-4 mr-2" />
                End Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Button */}
        <Button variant="ghost" size="sm">
          <Settings className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}